import type { NotificationSettings } from '../types';

const NOTIFICATION_KEY = 'attendify_notifications';
const REMINDER_MINUTES_BEFORE = 5; // Remind 5 minutes before shift start

let reminderTimeoutId: ReturnType<typeof setTimeout> | null = null;
let lateCheckTimeoutId: ReturnType<typeof setTimeout> | null = null;
let shiftEndReminderTimeoutId: ReturnType<typeof setTimeout> | null = null;

function getSettings(): NotificationSettings {
  const raw = localStorage.getItem(NOTIFICATION_KEY);
  if (raw) return JSON.parse(raw) as NotificationSettings;
  return {
    enabled: false,
    permission: ('Notification' in window) ? Notification.permission : 'denied',
  };
}

function saveSettings(settings: NotificationSettings): void {
  localStorage.setItem(NOTIFICATION_KEY, JSON.stringify(settings));
}

function cancelReminderTimeout(): void {
  if (reminderTimeoutId !== null) {
    clearTimeout(reminderTimeoutId);
    reminderTimeoutId = null;
  }
  if (lateCheckTimeoutId !== null) {
    clearTimeout(lateCheckTimeoutId);
    lateCheckTimeoutId = null;
  }
}

function cancelShiftEndReminderTimeout(): void {
  if (shiftEndReminderTimeoutId !== null) {
    clearTimeout(shiftEndReminderTimeoutId);
    shiftEndReminderTimeoutId = null;
  }
}

async function showPushNotification(
  title: string,
  body: string,
  tag: string = 'attendance-reminder',
  icon?: string
): Promise<boolean> {
  if (!('Notification' in window)) return false;

  // Try service worker notification first (persistent, works when tab is unfocused).
  // navigator.serviceWorker.ready hangs forever when no SW is registered (e.g. dev mode),
  // so we race it against a short timeout and fall through to the standard API if needed.
  if ('serviceWorker' in navigator) {
    try {
      const swReady: Promise<ServiceWorkerRegistration> = Promise.race([
        navigator.serviceWorker.ready,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('SW not ready within timeout')), 600)
        ),
      ]);
      const registration = await swReady;
      // `renotify` is valid per spec but missing from TS lib typings — cast to include it.
      const swOpts = {
        body,
        icon: icon ?? '/logo192.png',
        badge: '/logo192.png',
        tag,
        renotify: true,     // re-alert user even when replacing a same-tag notification
        requireInteraction: tag === 'attendance-late',
        data: { url: '/' },
      } as NotificationOptions;
      await registration.showNotification(title, swOpts);
      return true;
    } catch {
      // Fall through to standard notification
    }
  }

  // Fallback: standard Notification API
  if (Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon: icon ?? '/logo192.png',
      tag,
    });
    return true;
  }

  return false;
}

export const notificationManager = {
  isSupported(): boolean {
    return 'Notification' in window;
  },

  getCurrentPermission(): NotificationPermission {
    if (!this.isSupported()) return 'denied';
    return Notification.permission;
  },

  getSettings(): NotificationSettings {
    return getSettings();
  },

  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) return 'denied';
    const permission = await Notification.requestPermission();
    const settings = getSettings();
    settings.permission = permission;
    settings.enabled = permission === 'granted';
    saveSettings(settings);
    return permission;
  },

  setEnabled(enabled: boolean): void {
    const settings = getSettings();
    settings.enabled = enabled;
    saveSettings(settings);
    if (!enabled) cancelReminderTimeout();
  },

  /**
   * Schedule a check-in reminder BEFORE today's shift start (5 minutes before) 
   * AND a late notification AT shift start if not checked in.
   * @param shiftStartTime - "HH:mm" format e.g. "09:00"
   * @param isAlreadyCheckedIn - if true, no reminder needed
   */
  scheduleCheckInReminder(shiftStartTime: string, isAlreadyCheckedIn: boolean): void {
    cancelReminderTimeout();

    if (!this.isSupported()) return;
    if (this.getCurrentPermission() !== 'granted') return;
    if (!getSettings().enabled) return;
    if (isAlreadyCheckedIn) return;

    const [hours, minutes] = shiftStartTime.split(':').map(Number);
    const now = new Date();
    
    // Calculate shift start time
    const shiftStart = new Date();
    shiftStart.setHours(hours, minutes, 0, 0);

    // Calculate reminder time (5 min before shift start)
    const reminderTime = new Date(shiftStart.getTime() - REMINDER_MINUTES_BEFORE * 60 * 1000);

    const msUntilShiftStart = shiftStart.getTime() - now.getTime();
    const msUntilReminder = reminderTime.getTime() - now.getTime();

    // Case 1: Shift already started (up to 2h ago) → schedule with a short delay so that
    // the initial API load has time to confirm check-in status and cancel if already in.
    if (msUntilShiftStart < 0 && msUntilShiftStart >= -2 * 60 * 60 * 1000) {
      const lateMinutes = Math.round(Math.abs(msUntilShiftStart) / 60000);
      lateCheckTimeoutId = setTimeout(() => {
        void showPushNotification(
          '🚨 You\'re Late!',
          `Your shift started at ${shiftStartTime} (${lateMinutes} min ago). Check in now!`,
          'attendance-late'
        );
      }, 5000);
      return;
    }

    // Case 2: Reminder time hasn't passed yet → schedule pre-shift reminder
    if (msUntilReminder > 0) {
      reminderTimeoutId = setTimeout(() => {
        const records = JSON.parse(localStorage.getItem('attendify_attendance') ?? '[]') as Array<{ date: string; clockIn: string }>;
        const today = new Date().toISOString().split('T')[0];
        const checkedInToday = records.some((r) => r.date === today && r.clockIn);
        if (!checkedInToday) {
          void showPushNotification(
            '⏰ Shift Starting Soon',
            `Your shift starts at ${shiftStartTime} (in ${REMINDER_MINUTES_BEFORE} minutes). Don't forget to check in!`,
            'attendance-reminder'
          );
        }
      }, msUntilReminder);
    }

    // Case 3: Schedule late check notification at shift start time
    if (msUntilShiftStart > 0) {
      lateCheckTimeoutId = setTimeout(() => {
        const records = JSON.parse(localStorage.getItem('attendify_attendance') ?? '[]') as Array<{ date: string; clockIn: string }>;
        const today = new Date().toISOString().split('T')[0];
        const checkedInToday = records.some((r) => r.date === today && r.clockIn);
        if (!checkedInToday) {
          void showPushNotification(
            '🚨 You\'re Late!',
            `Your shift started at ${shiftStartTime}. Check in now to avoid being marked late!`,
            'attendance-late'
          );
        }
      }, msUntilShiftStart);
    }
  },

  /**
   * Send a notification when a user checks in late.
   * Called after check-in confirms late status.
   */
  async sendLateCheckInNotification(lateMinutes: number, shiftStartTime: string): Promise<void> {
    if (!this.isSupported()) return;
    if (this.getCurrentPermission() !== 'granted') return;
    if (!getSettings().enabled) return;

    await showPushNotification(
      '⚠️ Late Check-In Recorded',
      `You checked in ${lateMinutes} minute${lateMinutes !== 1 ? 's' : ''} after your shift start (${shiftStartTime}). Try to arrive earlier tomorrow!`,
      'attendance-late-checkin'
    );
  },

  cancelReminder(): void {
    cancelReminderTimeout();
  },

  /**
   * Schedule a reminder notification 5 minutes before shift end.
   * @param shiftEndTime   - "HH:mm" format e.g. "02:00"
   * @param isCheckedOut   - if true, no reminder needed (already checked out)
   * @param shiftStartTime - optional "HH:mm"; used to detect overnight shifts (e.g. "17:00")
   */
  scheduleShiftEndReminder(shiftEndTime: string, isCheckedOut: boolean, shiftStartTime?: string): void {
    cancelShiftEndReminderTimeout();

    if (!this.isSupported()) return;
    if (this.getCurrentPermission() !== 'granted') return;
    if (!getSettings().enabled) return;
    if (isCheckedOut) return;

    const [hours, minutes] = shiftEndTime.split(':').map(Number);
    const now = new Date();

    const shiftEnd = new Date();
    shiftEnd.setHours(hours, minutes, 0, 0);

    // Overnight shift (e.g. 17:00–02:00): shift end falls on the NEXT calendar day
    // when the current time is still in the evening portion (after shift start hour).
    if (shiftStartTime) {
      const [sh, sm] = shiftStartTime.split(':').map(Number);
      const isOvernight = hours * 60 + minutes < sh * 60 + sm;
      if (isOvernight) {
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        if (nowMinutes >= sh * 60 + sm) {
          shiftEnd.setDate(shiftEnd.getDate() + 1);
        }
      }
    }

    // Reminder fires 5 minutes before shift end
    const reminderTime = new Date(shiftEnd.getTime() - REMINDER_MINUTES_BEFORE * 60 * 1000);
    const msUntilReminder = reminderTime.getTime() - now.getTime();

    if (msUntilReminder <= 0) return; // Notification time has already passed

    shiftEndReminderTimeoutId = setTimeout(() => {
      void showPushNotification(
        '⏳ Shift Ending Soon',
        `Your shift ends at ${shiftEndTime} (in ${REMINDER_MINUTES_BEFORE} minutes). Don't forget to check out!`,
        'attendance-shift-end'
      );
    }, msUntilReminder);
  },

  cancelShiftEndReminder(): void {
    cancelShiftEndReminderTimeout();
  },

  async sendTestNotification(): Promise<boolean> {
    // Use a unique tag per invocation so the browser never silently deduplicates
    // the notification against a previously-shown one with the same tag.
    const uniqueTag = `attendance-test-${Date.now()}`;
    return showPushNotification(
      '✅ Attendify Test Notification',
      `Notifications are working! You'll be reminded ${REMINDER_MINUTES_BEFORE} min before your shift and alerted if you're late.`,
      uniqueTag
    );
  },
};
